import { guestCount, groupSizeBucket, estimatePrice } from './discover-pricing.mjs';
import { cleanDiscoverParams, writeDiscoverHistory } from './discover-url.mjs';

document.addEventListener('DOMContentLoaded', () => {
    // Enhanced Progressive Filters Manager
    class ProgressiveFiltersManager {
      constructor() {
        this.filtersForm = document.getElementById('filters_form');
        this.typeSelect = document.getElementById('type-select');
        this.placeholderLocation = document.getElementById('placeholder-location');
        this.locationFilter = document.getElementById('location-filter');
        this.cityFilterSelect = document.getElementById('city-filter-select');
        this.sortSelect = document.getElementById('sort-select');
        this.collectionInput = document.getElementById('collection-slug-input');
        this.collectionPills = Array.from(document.querySelectorAll('[data-collection-slug]'));
        this.initialLoadComplete = false;
        this.currentRequest = null;
        this.lastRequestUrl = null;
        this.pendingUpdate = null;

        // Filter modal state
        this.filtersTriggerBtn = document.querySelector('.filter-pill--trigger');
        this.filtersModalEl = document.getElementById('filtersModal');
        this.activePillsContainer = document.querySelector('.filter-active-pills-group');
        // Types implied by the page itself (/activities/in-person, /activities/virtual)
        // are not user-applied filters: never render them as chips or count them
        // in the trigger badge. Mirrors the server-side chip_types logic.
        this.premiseTypes = ((this.activePillsContainer &&
          this.activePillsContainer.dataset.premiseTypes) || '')
          .split(',').map((s) => s.trim()).filter(Boolean);
        this.priceCustomToggle = document.querySelector('[data-price-custom-toggle]');
        this.priceCustomPanel = document.querySelector('[data-price-custom-panel]');
        this.priceMinInput = document.getElementById('price-min-input');
        this.priceMaxInput = document.getElementById('price-max-input');
        this.applyCustomPriceDebounced = this.debounce(options => this.applyCustomPriceFilter(options), 400);

        this.durationCustomToggle = document.querySelector('[data-duration-custom-toggle]');
        this.durationCustomPanel = document.querySelector('[data-duration-custom-panel]');
        this.durationMinInput = document.getElementById('duration-min-input');
        this.durationMaxInput = document.getElementById('duration-max-input');
        this.applyCustomDurationDebounced = this.debounce(() => this.applyCustomDurationFilter(), 400);

        // Maps filter param names to their authoritative hidden input IDs
        // All multi-select params use dedicated hidden inputs storing JSON arrays
        this.filterInputIds = {
          collection_slug: 'collection-slug-input',
          type: 'type-hidden-input',
          price: 'price-hidden-input',
          duration: 'duration-hidden-input',
          group_size: 'group-size-hidden-input',
          venue_type: 'venue-type-hidden-input',
          insurance_coverage: 'insurance-coverage-hidden-input',
          listing_type: 'listing-type-hidden-input',
          city_location: 'city-filter-hidden-input',
          platform_location: 'platform-location-hidden-input'
        };

        // Slug → display name map, built from collection pill DOM on init
        this.collectionNames = {};

        // Human-readable labels for filter values
        this.filterValueLabels = {
          insurance_coverage: { '1': 'Insured' },
          price: {
            '100_plus': '$100 or more',
            '100': '$50 to $100',
            '50': '$30 to $50',
            '30': '$30 or less',
            'free': '$0'
          },
          duration: {
            '90_plus': 'over 90 minutes',
            '60_90': '60 - 90 minutes',
            '30_60': '30 - 60 minutes',
            '0_30': '0 - 30 minutes'
          },
          group_size: {
            '1_10': '1 - 10 guests',
            '11_25': '11 - 25 guests',
            '26_50': '26 - 50 guests',
            '51_100': '51 - 100 guests',
            '101_plus': '101+ guests'
          },
          venue_type: {
            'includes_venue': 'Includes Venue',
            'comes_to_you': 'Comes to You'
          },
          listing_type: {
            'originals': 'Originals',
            'verified': 'Verified',
            'external_vendors': 'External Vendors'
          },
          type: {
            'In-Person': 'In-Person',
            'Virtual': 'Virtual'
          }
        };

        this.init();
      }

      init() {
        const params = this.serializeParams(new URLSearchParams(window.location.search));
        writeDiscoverHistory(window.location.pathname + (params ? `?${params}` : '') + window.location.hash, { replace: true });
        this.initializeTooltips();
        this.buildCollectionNamesMap();
        this.bindEvents();
        this.updateLocationFilters();
        
        this.initialLoadComplete = true;
        this.initializePaginationLinks();
      }

      buildCollectionNamesMap() {
        this.collectionPills.forEach(pill => {
          const slug = pill.dataset.collectionSlug;
          const name = pill.dataset.collectionName || pill.textContent.trim();
          if (slug) this.collectionNames[slug] = name;
        });

        // Occasion buttons in the filters modal also select collection slugs;
        // register their names so their active pills read as labels, not slugs.
        document.querySelectorAll('[data-filter-param="collection_slug"][data-collection-name]').forEach(btn => {
          const slug = btn.dataset.filterValue;
          if (slug) this.collectionNames[slug] = btn.dataset.collectionName;
        });
      }

      initializeTooltips() {
        // Check if bootstrap is available
        if (!window.bootstrap || !window.bootstrap.Tooltip) {
          console.warn('Bootstrap not loaded, skipping tooltip initialization');
          return;
        }

        // MutationObserver to catch tooltip creation and apply guarantee styling
        if (typeof MutationObserver !== 'undefined' && !this.tooltipObserver) {
          const tooltipObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1 && node.classList && node.classList.contains('tooltip')) {
                  const trigger = document.querySelector('.guarantee-tooltip-trigger[aria-describedby="' + node.id + '"]');
                  if (trigger) {
                    node.classList.add('guarantee-tooltip');
                  }
                }
              });
            });
          });
          tooltipObserver.observe(document.body, { childList: true, subtree: true });
          this.tooltipObserver = tooltipObserver;
        }
        
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
          // Dispose any existing tooltip instance to prevent duplicates
          const existingInstance = bootstrap.Tooltip.getInstance(tooltipTriggerEl);
          if (existingInstance) {
            existingInstance.dispose();
          }

          var tooltipOptions = {
            html: true  // Enable HTML content in tooltips
          };
          
          // Set custom offset for guarantee tooltips to appear right below and centered
          if (tooltipTriggerEl.classList.contains('guarantee-tooltip-trigger')) {
            tooltipOptions = {
              html: true,
              offset: [0, 8],
              customClass: 'guarantee-tooltip',
              popperConfig: {
                placement: 'bottom',
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8]
                    }
                  },
                  {
                    name: 'computeStyles',
                    options: {
                      adaptive: false,
                      roundOffsets: true
                    }
                  }
                ]
              }
            };
          }
          
          var tooltip = new bootstrap.Tooltip(tooltipTriggerEl, tooltipOptions);
          
          // Handle guarantee tooltip styling and positioning
          if (tooltipTriggerEl.classList.contains('guarantee-tooltip-trigger')) {
            tooltipTriggerEl.addEventListener('inserted.bs.tooltip', function() {
              var tooltipId = tooltipTriggerEl.getAttribute('aria-describedby');
              if (tooltipId) {
                var tooltipElement = document.getElementById(tooltipId);
                if (tooltipElement) {
                  tooltipElement.classList.add('guarantee-tooltip');
                }
              }
            });
            
            tooltipTriggerEl.addEventListener('shown.bs.tooltip', function() {
              var tooltipId = tooltipTriggerEl.getAttribute('aria-describedby');
              if (tooltipId) {
                var tooltipElement = document.getElementById(tooltipId);
                if (tooltipElement) {
                  tooltipElement.classList.add('guarantee-tooltip');
                  // Ensure tooltip has high z-index
                  tooltipElement.style.zIndex = '9999';
                  
                  // Adjust positioning to center if needed (using requestAnimationFrame to ensure Bootstrap has positioned it first)
                  requestAnimationFrame(function() {
                    var triggerRect = tooltipTriggerEl.getBoundingClientRect();
                    var tooltipRect = tooltipElement.getBoundingClientRect();
                    var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                    var centeredLeft = triggerRect.left + scrollLeft + (triggerRect.width / 2) - (tooltipRect.width / 2);
                    var left = Math.max(scrollLeft + 8, Math.min(centeredLeft, scrollLeft + window.innerWidth - tooltipRect.width - 8));
                    
                    // Only adjust if significantly off-center (more than 5px)
                    var currentLeft = parseFloat(tooltipElement.style.left) || tooltipRect.left + scrollLeft;
                    if (Math.abs(currentLeft - left) > 5) {
                      tooltipElement.style.left = left + 'px';
                    }
                  });
                }
              }
            });
          }
          
          return tooltip;
        });

        this.initializePopovers();
      }

      // Click-to-open popovers (e.g. the Team Building Score explainer). Click
      // trigger keeps links inside the popover usable; a document listener
      // dismisses on outside click.
      initializePopovers() {
        if (!window.bootstrap || !window.bootstrap.Popover) return;

        document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
          const existing = window.bootstrap.Popover.getInstance(el);
          if (existing) existing.dispose();
          new window.bootstrap.Popover(el);
        });

        if (!document.body.dataset.popoverDismissBound) {
          document.body.dataset.popoverDismissBound = 'true';
          document.addEventListener('click', (e) => {
            document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
              const pop = window.bootstrap.Popover.getInstance(el);
              if (!pop) return;
              if (el.contains(e.target)) return;
              if (pop.tip && pop.tip.contains(e.target)) return;
              pop.hide();
            });
          });
        }
      }

      updateLocationFilters() {
        const selectedType = this.typeSelect?.value;
        const urlParams = new URLSearchParams(window.location.search);
        const isSearch = urlParams.get('search') === 'true';
        const locationName = urlParams.get('location_name');

        if (isSearch && locationName) {
          if (this.typeSelect) this.typeSelect.value = 'In-Person';
          if (this.placeholderLocation) this.placeholderLocation.style.display = 'block';
          if (this.locationFilter) this.locationFilter.style.display = 'none';
        } else {
          if (this.placeholderLocation) this.placeholderLocation.style.display = 'none';

          if (selectedType === 'In-Person') {
            if (this.locationFilter) this.locationFilter.style.display = 'block';
          } else if (selectedType === 'Virtual' || selectedType === 'Hybrid') {
            if (this.locationFilter) this.locationFilter.style.display = 'none';
          } else if (selectedType === 'All Experiences' || selectedType === '') {
            if (this.placeholderLocation) this.placeholderLocation.style.display = 'block';
            if (this.locationFilter) this.locationFilter.style.display = 'none';
          }
        }
      }

      bindEvents() {
        document.querySelectorAll('[data-search-filter-checkbox]').forEach(control => {
          control.addEventListener('change', () => {
            const param = control.dataset.searchFilterCheckbox;
            this.setFilterValues(param, control.checked ? [control.value] : []);
            this.updateModalGroupActiveState(param, this.getFilterValues(param));
            this.syncActivePills();
            this.syncFilterTriggerBadge();
            const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
            this.updateUrlAndSubmitImmediate(searchParams);
          });
        });
        ['discover-search-duration', 'discover-search-platform'].forEach(id => {
          document.getElementById(id)?.addEventListener('change', event => {
            this.applySidebarFilter(event.target);
          });
        });
        const headerGuests = document.getElementById('discover-search-guests');
        const applyGuests = this.debounce(() => {
          if (headerGuests?.checkValidity()) this.applySidebarFilter(headerGuests);
        }, 400);
        headerGuests?.addEventListener('input', () => {
          document.querySelectorAll('[data-filter-param="guests"]').forEach(input => {
            if (input !== headerGuests) input.value = headerGuests.value;
          });
          this.updatePriceEstimates();
          applyGuests();
        });
        ['min', 'max'].forEach(bound => {
          document.getElementById(`discover-search-budget-${bound}`)?.addEventListener('input', event => {
            const input = bound === 'min' ? this.priceMinInput : this.priceMaxInput;
            if (input) input.value = event.target.value;
            this.setFilterValues('price', []);
            this.updateModalGroupActiveState('price', []);
            this.applyCustomPriceDebounced({ fromSearchBar: true });
          });
        });
        // Bind type select change event
        if (this.typeSelect) {
          this.typeSelect.addEventListener('change', () => this.handleTypeChange());
        }

        // Bind form change events
        if (this.filtersForm) {
          this.filtersForm.addEventListener('change', (event) => this.handleFormChange(event));
        }

        this.collectionPills.forEach((pill) => {
          pill.addEventListener('click', (event) => this.handleCollectionPillToggle(event));
        });

        // Bind other filter changes to set search=false
        [this.locationFilter, this.cityFilterSelect].forEach(filter => {
          if (filter) {
            filter.addEventListener('change', () => this.setSearchFalseInUrl());
          }
        });

        // Handle browser back/forward navigation
        window.addEventListener('popstate', () => {
          this.syncCollectionStateFromUrl();
          const params = new URLSearchParams(window.location.search);
          [['price-min-input', 'price_min'], ['price-max-input', 'price_max'],
            ['duration-min-input', 'duration_min'], ['duration-max-input', 'duration_max']].forEach(([id, param]) => {
            const input = document.getElementById(id);
            if (input) input.value = params.get(param) || '';
          });
          document.querySelectorAll('[data-filter-param="guests"]').forEach(input => { input.value = params.get('guests') || ''; });
          document.querySelectorAll('[data-search-filter-checkbox]').forEach(input => {
            input.checked = this.getFilterValues(input.dataset.searchFilterCheckbox).includes(input.value);
          });
          this.syncSidebarMirrorControls();
          this.updateResults(this.getCurrentPageFromUrl(), false);
        });

        // Filters trigger pill → open modal
        if (this.filtersTriggerBtn) {
          this.filtersTriggerBtn.addEventListener('click', (e) => this.handleFiltersTriggerClick(e));
        }

        // Filter option buttons + footer buttons inside modal (delegated)
        if (this.filtersModalEl) {
          this.filtersModalEl.addEventListener('click', (e) => {
            const priceCustomToggle = e.target.closest('[data-price-custom-toggle]');
            if (priceCustomToggle) {
              this.handlePriceCustomToggleClick(e, priceCustomToggle);
              return;
            }
            const durationCustomToggle = e.target.closest('[data-duration-custom-toggle]');
            if (durationCustomToggle) {
              this.handleDurationCustomToggleClick(e, durationCustomToggle);
              return;
            }
            const optionBtn = e.target.closest('.filter-option-btn');
            if (optionBtn) { this.handleFilterOptionClick(e, optionBtn); return; }
          });

          this.filtersModalEl.addEventListener('input', (e) => {
            if (e.target.matches('#price-min-input, #price-max-input')) {
              this.applyCustomPriceDebounced();
            }
            if (e.target.matches('#duration-min-input, #duration-max-input')) {
              this.applyCustomDurationDebounced();
            }
          });

          // Guest-count input in the modal behaves exactly like the sidebar's
          this.filtersModalEl.addEventListener('change', (e) => {
            const control = e.target.closest('[data-filter-param="guests"]');
            if (control) this.applySidebarFilter(control);
          });
        }

        // Apply / Clear all buttons in the modal footer
        const applyBtn = document.getElementById('filtersModalApplyBtn');
        if (applyBtn) {
          applyBtn.addEventListener('click', (e) => this.handleApplyFiltersClick(e));
        }
        const clearBtn = document.getElementById('filtersModalClearBtn');
        if (clearBtn) {
          clearBtn.addEventListener('click', (e) => this.handleClearAllFiltersClick(e));
        }

        // Empty-state "Clear all filters" button — delegated on document because
        // the empty state is re-rendered inside the AJAX results wrapper.
        document.addEventListener('click', (e) => {
          const emptyClearBtn = e.target.closest('.discover-empty-state__clear');
          if (emptyClearBtn) this.handleClearAllFiltersClick(e);
        });

        // Remove filter pills (delegated from pills row)
        const pillsRow = document.getElementById('filterPillsRow');
        if (pillsRow) {
          pillsRow.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('.filter-pill__remove');
            if (removeBtn) this.handleRemoveFilterPill(e, removeBtn);
          });
        }

        // Scroll buttons
        const scrollBtnRight = document.getElementById('filterPillsScrollBtn');
        const scrollBtnLeft  = document.getElementById('filterPillsScrollBtnLeft');
        if (pillsRow) {
          if (scrollBtnRight) {
            scrollBtnRight.addEventListener('click', () => {
              pillsRow.scrollBy({ left: 200, behavior: 'smooth' });
            });
          }
          if (scrollBtnLeft) {
            scrollBtnLeft.addEventListener('click', () => {
              pillsRow.scrollBy({ left: -200, behavior: 'smooth' });
            });
          }
          pillsRow.addEventListener('scroll', () => this.updatePillsScrollState(pillsRow), { passive: true });
          requestAnimationFrame(() => this.updatePillsScrollState(pillsRow));
        }
      }

      handleTypeChange() {
        const searchParams = this.updateUrlParams();
        
        // Handle location-related parameters for type changes
        ['search', 'latitude', 'lat', 'longitude', 'lng', 'location_name'].forEach(param => {
          searchParams.delete(param);
        });

        // Set type parameter
        if (this.typeSelect.value === 'All Experiences') {
          searchParams.delete('type');
        } else {
          searchParams.set('type', this.typeSelect.value);
        }

        // Clear location inputs
        const locationInputs = ['latitude', 'longitude', 'location-name'];
        locationInputs.forEach(inputId => {
          const input = document.getElementById(inputId);
          if (input) input.value = '';
        });

        this.updateLocationFilters();
        this.updateUrlAndSubmit(searchParams);
      }

      setSearchFalseInUrl() {
        const searchParams = this.updateUrlParams();
        searchParams.set('search', 'false');
        this.updateUrl(searchParams);
      }

      updateUrlParams() {
        const currentUrl = new URL(window.location.href);
        return new URLSearchParams(currentUrl.search);
      }

      updateUrl(searchParams) {
        const basePath = this.determineBasePath(searchParams);
        const serializedParams = this.serializeParams(searchParams, basePath);
        const newUrl = serializedParams ? `${basePath}?${serializedParams}` : basePath;
        writeDiscoverHistory(newUrl);
      }

      updateUrlAndSubmit(searchParams, page = 1) {
        searchParams.set('page', page);
        this.updateUrl(searchParams);
        this.updateResults(page, false);
      }

      updateUrlAndSubmitImmediate(searchParams, page = 1) {
        searchParams.set('page', page);
        this.updateUrl(searchParams);
        // Force immediate execution by clearing any pending debounce
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
          this.pendingUpdate = null;
        }
        // Execute immediately without debounce
        const fadingContent = document.getElementById('fading-content');
        if (fadingContent) {
          fadingContent.classList.add('fade-out');
          fadingContent.classList.remove('fade-in');
        }
        this.performFilterRequest(page, false, fadingContent);
      }

      handleFormChange(event) {
        event.preventDefault();

        if (event.target.type === 'checkbox') {
          this.handleCheckboxChange(event.target);
        } else if (event.target.type === 'select-one') {
          this.handleSelectChange(event.target);
        } else {
          this.updateResults(1, false);
        }
      }

      handleCheckboxChange(checkbox) {
        const paramName = checkbox.name.replace('[]', '');
        const searchParams = this.updateUrlParams();

        // Get all checked checkboxes for this parameter
        const checkedValues = Array.from(document.querySelectorAll(`input[name="${checkbox.name}"]:checked`))
          .map(input => input.value);

        // Update or remove the parameter based on checked values
        searchParams.delete(paramName);
        if (checkedValues.length > 0) {
          searchParams.set(paramName, JSON.stringify(checkedValues));
        }

        this.syncCollectionPillHrefs(searchParams);

        // For checkboxes, apply immediately without debounce
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      handleSelectChange(select) {
        const paramName = select.name;
        const searchParams = this.updateUrlParams();

        // Trim value to handle whitespace-only values as empty
        const value = select.value ? select.value.trim() : '';

        if (value && value !== '') {
          searchParams.set(paramName, value);
        } else {
          searchParams.delete(paramName);
        }

        this.syncCollectionPillHrefs(searchParams);

        // For select dropdowns, apply immediately without debounce
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      handleCollectionPillToggle(event) {
        event.preventDefault();
        const pill = event.currentTarget;
        const slug = pill.dataset.collectionSlug;
        if (!slug) return;

        // Toggle this slug in the multi-select collection array
        this.toggleFilterValue('collection_slug', slug);

        // Update collection pill visual states
        this.syncCollectionPillActiveStates();

        // Rebuild active pills + badge
        this.syncActivePills();
        this.syncFilterTriggerBadge();

        // Show spinner and submit
        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      syncCollectionPillActiveStates() {
        const selected = this.getFilterValues('collection_slug');
        this.collectionPills.forEach(pill => {
          const slug = pill.dataset.collectionSlug || '';
          // Hide the pill when selected (the active pill with × represents it instead)
          pill.style.display = selected.includes(slug) ? 'none' : '';
        });
      }

      handleSortChange() {
        this.updateResults(1, false);
      }

      // Enhanced updateResults method with progressive enhancement
      updateResults(page = 1, scrollToTop = false) {
        // Clear any pending debounce timer
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
          this.pendingUpdate = null;
        }
        
        const fadingContent = document.getElementById('fading-content');

        // Fade out with visual feedback (runs in parallel with fetch)
        if (fadingContent) {
          fadingContent.classList.add('fade-out');
          fadingContent.classList.remove('fade-in');
        }

        // Execute filter request immediately for all changes
        // Debouncing was causing filters to not apply consistently
        this.performFilterRequest(page, scrollToTop, fadingContent);
      }

      performFilterRequest(page, scrollToTop, fadingContent) {
        const formData = new FormData(this.filtersForm);
        const searchParams = this.buildSearchParams(formData, page);
        const requestUrl = this.buildRequestUrl(searchParams);

        // Ensure we get the .js format response which includes HTML
        const jsRequestUrl = requestUrl.includes('?') 
          ? requestUrl.replace('?', '.js?')
          : requestUrl + '.js';

        // Deduplicate: skip if same URL as last request
        if (jsRequestUrl === this.lastRequestUrl) {
          if (fadingContent) {
            fadingContent.classList.remove('fade-out');
            fadingContent.classList.add('fade-in');
          }
          this.hideLoadingSpinner();
          return;
        }

        this.lastRequestUrl = jsRequestUrl;
        this.currentRequest?.abort();

        // Show loading spinner
        this.showLoadingSpinner();

        // Fast responses keep the subtle fade; only a slow one swaps the stale
        // cards for skeletons.
        this.clearResultsSkeleton();
        this.skeletonTimer = setTimeout(() => this.showResultsSkeleton(), 400);

        // Create abortable request
        const controller = new AbortController();
        this.currentRequest = controller;
        
        fetch(jsRequestUrl, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: controller.signal
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, url: ${jsRequestUrl}`);
          }
          return response.json();
        })
        .then(data => {
          if (controller.signal.aborted || this.currentRequest !== controller) return;
          // Use the original requestUrl (without .js) for browser URL display
          this.handleSuccessResponse(data, fadingContent, scrollToTop, requestUrl);
        })
        .catch(error => {
          if (controller.signal.aborted || this.currentRequest !== controller) return;
          this.lastRequestUrl = null;
          this.handleErrorResponse(error, fadingContent, jsRequestUrl);
        })
        .finally(() => {
          if (this.currentRequest === controller) this.currentRequest = null;
        });
      }

      buildSearchParams(formData, page) {
        const url = new URL(window.location.href);
        const searchParams = new URLSearchParams(url.search);

        // Set page parameter
        searchParams.set('page', page);

        // Multi-select filter params — read directly from their authoritative hidden inputs
        // to bypass duplicate-name issues (e.g. type-hidden-input vs type-select)
        const multiSelectParams = Object.keys(this.filterInputIds);
        multiSelectParams.forEach(param => {
          const inputEl = document.getElementById(this.filterInputIds[param]);
          const value = inputEl ? inputEl.value.trim() : '';
          if (!value) {
            searchParams.delete(param);
          } else {
            searchParams.set(param, value);
          }
        });

        // Simple scalar params
        const simpleParams = ['rating', 'sort'];

        simpleParams.forEach(param => {
          const value = formData.get(param);
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            searchParams.delete(param);
          } else {
            searchParams.set(param, value);
          }
        });

        // Custom price/duration inputs live in the filters modal, which is moved to <body>
        // outside #filters_form — read them directly instead of FormData.
        this.syncGuestSearchParams(searchParams);
        this.syncCustomPriceSearchParams(searchParams);
        this.syncCustomDurationSearchParams(searchParams);

        // Handle array parameters (checkboxes only - vibes and categories)
        const arrayParams = ['vibe_ids', 'category_ids'];
        const paramValues = {};

        for (let [key, value] of formData.entries()) {
          if (value && key !== 'type') {  // Skip 'type' as we've already handled it
            const paramName = key.replace('[]', '');
            if (arrayParams.includes(paramName)) {
              if (!paramValues[paramName]) {
                paramValues[paramName] = [];
              }
              paramValues[paramName].push(value);
            }
          }
        }

        // Set array parameters as JSON
        for (const [key, values] of Object.entries(paramValues)) {
          if (values.length > 0) {
            searchParams.set(key, JSON.stringify(values));
          }
        }

        // Handle sort parameter (skip if 'magic' - it's the default)
        if (this.sortSelect?.value && this.sortSelect.value !== 'magic') {
          searchParams.set('sort', this.sortSelect.value);
        } else {
          searchParams.delete('sort');
        }

        // Preserve existing location-related parameters
        ['lat', 'lng', 'latitude', 'longitude', 'location_name', 'city', 'state', 'country'].forEach(param => {
          const existingValue = searchParams.get(param);
          if (existingValue && !formData.has(param)) {
            formData.append(param, existingValue);
          }
        });

        // Clean up default parameters
        if (searchParams.get('type') === 'All Experiences') {
          searchParams.delete('type');
        }
        if (searchParams.get('sort') === 'magic') {
          searchParams.delete('sort');
        }

        return this.orderParams(searchParams);
      }

      buildRequestUrl(searchParams) {
        const params = this.serializeParams(searchParams);
        // Remove any trailing ? if params is empty
        const baseUrl = window.location.pathname;
        if (!params || params.trim() === '') {
          return baseUrl;
        }

        return `${baseUrl}?${params}`;
      }

      orderParams(searchParams) {
        const source = new URLSearchParams(searchParams.toString());
        const ordered = new URLSearchParams();
        const flagKeys = ['search'];

        const appendAlias = (targetKey, aliases) => {
          for (const alias of aliases) {
            const values = source.getAll(alias);
            if (values.length > 0) {
              values.forEach(value => ordered.append(targetKey, value));
              aliases.forEach(name => source.delete(name));
              return;
            }
          }
        };

        const appendKey = (key) => {
          const values = source.getAll(key);
          if (values.length === 0) {
            return;
          }

          values.forEach(value => ordered.append(key, value));
          source.delete(key);
        };

        appendAlias('q', ['q', 'query']);
        appendAlias('lat', ['lat', 'latitude']);
        appendAlias('lng', ['lng', 'longitude']);
        appendKey('type');

        const otherKeys = [];
        source.forEach((_, key) => {
          if (key === 'sort' || key === 'page' || flagKeys.includes(key)) {
            return;
          }

          if (!otherKeys.includes(key)) {
            otherKeys.push(key);
          }
        });

        otherKeys.forEach(key => appendKey(key));

        appendKey('sort');
        appendKey('page');

        flagKeys.forEach(flag => appendKey(flag));

        const remainingEntries = [];
        source.forEach((value, key) => {
          remainingEntries.push([key, value]);
        });

        remainingEntries.forEach(([key, value]) => {
          ordered.append(key, value);
        });

        return ordered;
      }

      serializeParams(searchParams, pathname = window.location.pathname) {
        const ordered = this.orderParams(cleanDiscoverParams(searchParams, pathname));
        const serialized = ordered.toString();
        return serialized;
      }

      determineBasePath(searchParams) {
        const currentPath = window.location.pathname;

        if (this.isTeamBuildingLocationPath(currentPath)) {
          return currentPath;
        }

        if (!currentPath.startsWith('/activities')) {
          return currentPath;
        }

        const type = cleanDiscoverParams(searchParams, '/activities').get('type');
        if (type === 'In-Person') {
          return '/activities/in-person';
        }

        if (currentPath === '/activities/in-person' && !type) {
          return currentPath;
        }

        if (currentPath === '/activities/in-person') {
          return '/activities';
        }

        return '/activities';
      }

      isTeamBuildingLocationPath(path) {
        const locationRegex = /^\/activities\/(?!in-person(?:\/|$))[a-z0-9-]+(?:\/[a-z0-9-]+)?$/;
        return locationRegex.test(path);
      }

      handleSuccessResponse(data, fadingContent, scrollToTop, requestUrl) {
        // Update main content
        this.updateResultsContent(data);
        
        // Update counts and price info
        this.updateEventInfo(data);
        
        // Update banner content
        this.updateBannerContent(data);
        
        // Update pagination
        this.updatePagination(data);
        
        // Reinitialize components
        this.initializeTooltips();
        this.initializePaginationLinks();
        
        // Update URL state
        writeDiscoverHistory(requestUrl);
        this.syncCollectionStateFromUrl(requestUrl);
        
        // Handle visual feedback
        this.handleFadeIn(fadingContent);
        
        // Handle type updates
        this.handleTypeUpdate(data);
        
        // Update leaderboard (initial load only)
        this.updateLeaderboard(data);
        
        // Update map if available
        this.updateMap(data);

        // Update YelpBiz list
        this.updateBizList(data);
        
        // Handle scroll
        if (scrollToTop) {
          this.scrollToResults();
        }
      }

  clearResultsSkeleton() {
    if (this.skeletonTimer) {
      clearTimeout(this.skeletonTimer);
      this.skeletonTimer = null;
    }
    const resultsContainer = document.getElementById('event-results');
    if (resultsContainer) resultsContainer.removeAttribute('aria-busy');
  }

  showResultsSkeleton() {
    const resultsContainer = document.getElementById('event-results');
    if (!resultsContainer) return;

    const cardCount = Math.min(
      Math.max(resultsContainer.querySelectorAll('.ds-event-card').length, 3),
      9
    );
    const skeletonCard = `
      <div class="ds-event-card-skeleton" aria-hidden="true">
        <div class="ds-skeleton ds-event-card-skeleton__media"></div>
        <div class="ds-event-card-skeleton__body">
          <div class="ds-skeleton ds-event-card-skeleton__line"></div>
          <div class="ds-skeleton ds-event-card-skeleton__line ds-event-card-skeleton__line--short"></div>
        </div>
      </div>
    `;
    resultsContainer.setAttribute('aria-busy', 'true');
    resultsContainer.innerHTML = skeletonCard.repeat(cardCount);
  }

  updateResultsContent(data) {
    this.clearResultsSkeleton();
    const resultsContainer = document.getElementById('event-results');
    if (resultsContainer) {
      // Use a temporary container for parsing to avoid multiple reflows
      const temp = document.createElement('div');
      temp.innerHTML = data.html;

      // Clear and replace content in one operation
      resultsContainer.replaceChildren(...temp.childNodes);

      // No matching events: keep the server-rendered empty state if the
      // response included one, otherwise inject the same markup (must stay
      // in sync with platform/discover/_empty_state.html.erb).
      if (
        !resultsContainer.querySelector('.ds-event-card[data-latitude]') &&
        !resultsContainer.querySelector('[data-discover-empty-state]')
      ) {
        resultsContainer.innerHTML = `
          <div class="discover-empty-state" data-discover-empty-state style="grid-column: 1 / -1;">
            <h3 class="discover-empty-state__title">No activities match your filters</h3>
            <p class="discover-empty-state__hint">
              Try removing a filter or widening your price range &mdash; or tell us what
              you're planning and a real person will shortlist options for you, free.
            </p>
            <div class="discover-empty-state__actions">
              <button type="button" class="ds-btn ds-btn--primary discover-empty-state__clear">
                Clear all filters
              </button>
              <a href="https://calendly.com/client-advisor/quick-15-min-chat"
                 class="ds-btn ds-btn--outline js-advisor-call"
                 rel="noopener noreferrer nofollow">
                Get free help planning
              </a>
            </div>
          </div>
        `;
      }
    }
  }

      updateEventInfo(data) {
        // Hints reflect the full matching result set, without turning the
        // suggested range into a filter or overwriting the visitor's inputs.
        ['min', 'max'].forEach(bound => {
          const input = document.getElementById(`discover-search-budget-${bound}`);
          const price = data[`events_price_${bound}`];
          if (input && data.events_count !== undefined) {
            input.placeholder = data.events_count > 0 && price != null && Number.isFinite(Number(price))
              ? String(Number(price)) : 'Any';
          }
        });
        // Update the count container with pluralized text (matches the ERB "N activities")
        const eventsCountContainer = document.getElementById('events-count-container');
        if (eventsCountContainer && data.events_count !== undefined) {
          const pluralizedText = data.events_count === 1 ? 'activity' : 'activities';
          eventsCountContainer.textContent = `${data.events_count} ${pluralizedText}`;
        }
        this.updateApplyButtonLabel();
        // New result cards need their group-size totals + hearts rehydrated.
        this.updatePriceEstimates();
        if (typeof window.syncDiscoverShortlistHearts === 'function') {
          window.syncDiscoverShortlistHearts();
        }

        // Hide loading spinner after updating the HTML
        this.hideLoadingSpinner();
      }

      updateBannerContent(data) {
        const bannerTitle = document.querySelector('.discover-banner .title-text');
        if (bannerTitle && data.banner_title) {
          bannerTitle.innerHTML = data.banner_title;
        }

        const bannerDescription = document.querySelector('.discover-banner .desc');
        if (bannerDescription && data.banner_description) {
          bannerDescription.innerHTML = data.banner_description;
        }
      }

      updatePagination(data) {
        const paginationContainer = document.querySelector('.pagination-container');
        if (paginationContainer) {
          paginationContainer.innerHTML = data.pagination;
          document.dispatchEvent(new Event('discover:pagination-replaced'));
        }
      }

      updateBizList(data) {
        const newContent = data.yelp_biz_list;
        const existingSection = document.getElementById('biz-list');

        if (newContent && newContent.trim().length > 0) {
          if (existingSection) {
            existingSection.outerHTML = newContent;
          } else {
            const mapSection = document.getElementById('mapView');
            if (mapSection) {
              mapSection.insertAdjacentHTML('afterend', newContent);
            } else {
              const cityInsights = document.querySelector('.ds-city-insights');
              if (cityInsights) {
                cityInsights.insertAdjacentHTML('beforebegin', newContent);
              }
            }
          }
        } else if (existingSection) {
          existingSection.remove();
        }
      }

      handleFadeIn(fadingContent) {
        if (fadingContent) {
          fadingContent.classList.remove('fade-out');
          fadingContent.classList.add('fade-in');
        }
      }

      handleTypeUpdate(data) {
        if (data.default_type) {
          if (this.typeSelect) this.typeSelect.value = data.default_type;
          this.updateLocationFilters();
        }
      }

      updateLeaderboard(data) {
        if (!this.initialLoadComplete) {
          const leaderboardContainer = document.querySelector('.leaderboard-section');
          if (leaderboardContainer && data.leaderboard_html) {
            leaderboardContainer.innerHTML = data.leaderboard_html;
          }
        }
      }

      updateMap(data) {
        if (typeof updateMap === 'function' && data.events_data) {
          updateMap(data.events_data);
        }
      }

      scrollToResults() {
        setTimeout(() => {
          const listStart = document.getElementById('listStart');
          if (listStart) {
            listStart.scrollIntoView({ behavior: 'smooth' });
          }
        }, 350);
      }

      handleErrorResponse(error, fadingContent, requestUrl) {
        console.error('Filter request error:', error);
        console.error('Failed URL:', requestUrl);
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);

        // An aborted request means a newer one is in flight — its own timer
        // owns the skeleton now, so only a real failure clears it here.
        if (error.name !== 'AbortError') {
          this.clearResultsSkeleton();
        }

        // Hide loading spinner
        this.hideLoadingSpinner();
        
        // Always fade content back in, even on error
        this.handleFadeIn(fadingContent);
        
        // Show user-friendly error message
        if (error.name !== 'AbortError') {
          const resultsContainer = document.getElementById('event-results');
          if (resultsContainer) {
            resultsContainer.innerHTML = `
              <p class="my-4 text-center">
                <b>Sorry, there was an error loading the results. Please try again.</b>
              </p>
            `;
          }
        }
      }

      showLoadingSpinner() {
        const loader = document.getElementById('events-count-loader');
        const countContainer = document.getElementById('events-count-container');
        if (loader) {
          loader.style.display = 'inline-block';
        }
        if (countContainer) {
          countContainer.style.display = 'none';
        }
      }

      hideLoadingSpinner() {
        const loader = document.getElementById('events-count-loader');
        const countContainer = document.getElementById('events-count-container');
        if (loader) {
          loader.style.display = 'none';
        }
        if (countContainer) {
          countContainer.style.display = 'inline';
        }
      }

      updatePillsScrollState(row) {
        const scrollLeft  = row.scrollLeft;
        const hasOverflow = row.scrollWidth > row.clientWidth + 4;
        const atStart     = scrollLeft <= 4;
        const atEnd       = scrollLeft + row.clientWidth >= row.scrollWidth - 4;

        const wrapper      = row.closest('.filter-pills-wrapper');
        const btnRight     = document.getElementById('filterPillsScrollBtn');
        const btnLeft      = document.getElementById('filterPillsScrollBtnLeft');
        const fadeRight    = wrapper && wrapper.querySelector('.filter-pills-fade--right');
        const fadeLeft     = wrapper && wrapper.querySelector('.filter-pills-fade--left');

        if (wrapper) wrapper.classList.toggle('no-overflow', !hasOverflow);

        // Arrows are always visible; they just go inert at the extremes.
        if (btnRight)  btnRight.disabled = !hasOverflow || atEnd;
        if (btnLeft)   btnLeft.disabled  = !hasOverflow || atStart;
        if (fadeRight) fadeRight.classList.toggle('is-hidden', !hasOverflow || atEnd);
        if (fadeLeft)  fadeLeft.classList.toggle('is-hidden',  !hasOverflow || atStart);
      }

      // ---- Filter modal methods ----

      handleFiltersTriggerClick(event) {
        event.preventDefault();
        if (!this.filtersModalEl) return;

        // Move modal to <body> if it isn't already there.
        // Portal pages have overflow:hidden ancestors that trap fixed-position children,
        // preventing the modal backdrop and dialog from rendering correctly.
        if (this.filtersModalEl.parentElement !== document.body) {
          document.body.appendChild(this.filtersModalEl);
        }

        this.updateApplyButtonLabel();

        this.syncModalOptionStates();
        if (window.bootstrap && window.bootstrap.Modal) {
          const modal = window.bootstrap.Modal.getOrCreateInstance(this.filtersModalEl);
          modal.show();
        }
      }

      handleFilterOptionClick(event, btn) {
        event.preventDefault();
        const filterParam = btn.dataset.filterParam;
        const filterValue = btn.dataset.filterValue || '';
        if (!filterParam) return;

        if (filterParam === 'price') {
          this.clearCustomPrice();
          this.hidePriceCustomPanel();
        }

        if (filterParam === 'duration') {
          this.clearCustomDuration();
          this.hideDurationCustomPanel();
        }

        // Toggle this value in/out of the filter's selection array
        this.toggleFilterValue(filterParam, filterValue);

        // Update modal button active states for this group
        this.updateModalGroupActiveState(filterParam, this.getFilterValues(filterParam));

        // Occasion buttons share the collection_slug param with the pills row —
        // keep the pill active states in step.
        if (filterParam === 'collection_slug') {
          this.syncCollectionPillActiveStates();
        }

        // Update active pills + badge immediately (modal stays open)
        this.syncActivePills();
        this.syncFilterTriggerBadge();
        this.updateApplyButtonLabel();

        // Show spinner immediately, then apply filters
        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      // Applies one filter control's value. Named for the list-view sidebar it
      // was written for; since that sidebar was removed (discover_list_sidebar
      // test) the filters modal is the only caller.
      applySidebarFilter(control) {
        const param = control.dataset.filterParam;
        const value = (control.value || '').trim();
        const arrayParams = ['type', 'venue_type', 'listing_type', 'platform_location', 'collection_slug'];

        if (arrayParams.includes(param)) {
          const values = value ? [value] : [];
          this.setFilterValues(param, values);
          this.updateModalGroupActiveState(param, values);
          if (param === 'collection_slug') this.syncCollectionPillActiveStates();
        } else if (param === 'duration') {
          const values = value ? [value] : [];
          this.clearCustomDuration();
          this.hideDurationCustomPanel();
          this.setFilterValues('duration', values);
          this.updateModalGroupActiveState('duration', values);
        } else if (param === 'guests') {
          // Exact guest count (sidebar + modal); map it to the backend group_size bucket.
          const key = this.groupSizeBucketForCount(value);
          const values = key ? [key] : [];
          this.setFilterValues('group_size', values);
          this.updateModalGroupActiveState('group_size', values);
          // Keep the sidebar and modal guest inputs showing the same count
          document.querySelectorAll('[data-filter-param="guests"]').forEach(el => {
            if (el !== control) el.value = value;
          });
          this.updatePriceEstimates();
        } else if (param === 'price_min' || param === 'price_max') {
          // Sidebar price is a custom range: clear any preset price bucket and mirror
          // the value into the shared custom-price inputs (read by buildMultiFilterSearchParams).
          this.setFilterValues('price', []);
          this.updateModalGroupActiveState('price', []);
          const target = param === 'price_min' ? this.priceMinInput : this.priceMaxInput;
          if (target) target.value = value;
        } else {
          return;
        }

        this.syncActivePills();
        this.syncFilterTriggerBadge();
        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        if (typeof this.syncCollectionPillHrefs === 'function') this.syncCollectionPillHrefs(searchParams);
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      // Make the total the primary card price when the visitor sets a group
      // size. Exposed on window so load-more can hydrate newly appended cards.
      updatePriceEstimates() {
        const guests = this.currentGuestCount();
        document.querySelectorAll('.ds-event-card[data-estimate-per-person]').forEach(card => {
          const target = card.querySelector('[data-price-estimate]');
          const amount = card.querySelector('[data-price-amount]');
          const unit = card.querySelector('[data-price-unit]');
          if (!target || !amount || !unit) return;
          amount.dataset.originalPrice ??= amount.textContent;
          unit.dataset.originalUnit ??= unit.textContent;
          const estimate = estimatePrice(card.dataset, guests);
          if (!estimate) {
            amount.textContent = amount.dataset.originalPrice;
            unit.textContent = unit.dataset.originalUnit;
            const maximum = Number(card.dataset.estimateMaxGuests);
            const overCapacity = guests && maximum > 0 && guests > maximum;
            target.hidden = !overCapacity;
            target.textContent = overCapacity ? `Up to ${maximum} guests` : '';
            return;
          }
          amount.textContent = estimate.total.toLocaleString('en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2
          });
          unit.textContent = 'est. total';
          target.textContent = estimate.billedGuests > guests
            ? `For ${guests} guests · ${estimate.billedGuests}-guest minimum`
            : `For ${guests} ${guests === 1 ? 'guest' : 'guests'} · ${amount.dataset.originalPrice}${unit.dataset.originalUnit.replace('/ ', '/')}`;
          target.hidden = false;
        });
      }

      currentGuestCount() {
        return guestCount(Array.from(document.querySelectorAll('[data-filter-param="guests"]'))
          .map(input => input.value.trim()).find(Boolean));
      }

      syncGuestSearchParams(searchParams) {
        const guests = this.currentGuestCount();
        if (guests) searchParams.set('guests', guests);
        else searchParams.delete('guests');
      }

      // Reflect the current filter state back into the modal mirror controls
      // (guest count, price range) after a pill removal or clear-all changes
      // state outside those controls.
      syncSidebarMirrorControls(clearGuests = false) {
        [['discover-search-duration', 'duration'], ['discover-search-platform', 'platform_location']].forEach(([id, param]) => {
          const control = document.getElementById(id);
          if (control) control.value = this.getFilterValues(param)[0] || '';
        });
        if (clearGuests) {
          document.querySelectorAll('[data-filter-param="guests"]').forEach(el => { el.value = ''; });
        }
        this.updatePriceEstimates();
      }

      // Map an exact guest count to the backend group_size bucket key.
      groupSizeBucketForCount(value) {
        return groupSizeBucket(value);
      }

      handleApplyFiltersClick(event) {
        event.preventDefault();

        // Just close the modal — filters are already applied on each option click
        if (window.bootstrap && window.bootstrap.Modal) {
          const modal = window.bootstrap.Modal.getInstance(this.filtersModalEl);
          if (modal) modal.hide();
        }
      }

      handleClearAllFiltersClick(event) {
        event.preventDefault();

        this.clearCustomPrice();
        this.hidePriceCustomPanel();
        this.clearCustomDuration();
        this.hideDurationCustomPanel();

        Object.keys(this.filterInputIds).forEach(param => {
          this.setFilterValues(param, []);
          if (param !== 'collection_slug') {
            this.updateModalGroupActiveState(param, []);
          }
        });
        this.syncCollectionPillActiveStates();
        this.syncActivePills();
        this.syncFilterTriggerBadge();
        this.syncSidebarMirrorControls(true);

        // Show spinner and apply immediately
        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      // Mirror the live result count onto the modal's apply button ("Show 42
      // activities") so applying isn't a leap of faith.
      updateApplyButtonLabel() {
        const applyBtn = document.getElementById('filtersModalApplyBtn');
        const countEl = document.getElementById('events-count-container');
        if (!applyBtn) return;
        const text = countEl?.textContent?.trim();
        applyBtn.textContent = /^\d/.test(text || '') ? `Show ${text}` : 'Done';
      }

      countActiveFilterValues() {
        let total = 0;
        Object.keys(this.filterInputIds).forEach(param => {
          total += this.getFilterValues(param).filter(
            (v) => !(param === 'type' && this.premiseTypes.includes(v))
          ).length;
        });
        if (this.hasCustomPriceFilter()) total += 1;
        if (this.hasCustomDurationFilter()) total += 1;
        return total;
      }

      // Returns array of currently selected values for a filter param
      getFilterValues(filterParam) {
        const inputId = this.filterInputIds[filterParam];
        if (!inputId) return [];
        const input = document.getElementById(inputId);
        if (!input || !input.value) return [];
        const raw = input.value.trim();
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed.filter(Boolean) : (parsed ? [String(parsed)] : []);
        } catch {
          return raw ? [raw] : [];
        }
      }

      // Stores an array of values for a filter param (serialized as JSON)
      setFilterValues(filterParam, values) {
        if (!this.filtersForm) return;
        const clean = values.filter(v => v && v !== '');
        const serialized = clean.length > 0 ? JSON.stringify(clean) : '';

        this.filtersForm.querySelectorAll(`[name="${filterParam}"]`).forEach(input => {
          input.value = serialized;
        });

        // Keep legacy #type-select in sync for updateLocationFilters
        if (filterParam === 'type') {
          const legacySelect = document.getElementById('type-select');
          if (legacySelect) legacySelect.value = clean[0] || 'All Experiences';
        }
      }

      // Toggle a single value in a filter's array selection
      toggleFilterValue(filterParam, value) {
        const current = this.getFilterValues(filterParam);
        let next;
        if (!value) {
          next = [];
        } else if (current.includes(value)) {
          next = current.filter(v => v !== value);
        } else {
          next = [...current, value];
        }
        this.setFilterValues(filterParam, next);
        return next;
      }

      // Build URLSearchParams with all multi-select filter values as JSON strings
      buildMultiFilterSearchParams(searchParams) {
        Object.keys(this.filterInputIds).forEach(param => {
          const values = this.getFilterValues(param);
          if (values.length > 0) {
            // Single value: send as plain string; multiple: send as JSON array string
            searchParams.set(param, values.length === 1 ? values[0] : JSON.stringify(values));
          } else {
            searchParams.delete(param);
          }
        });
        this.syncGuestSearchParams(searchParams);
        this.syncCustomPriceSearchParams(searchParams);
        this.syncCustomDurationSearchParams(searchParams);
        return searchParams;
      }

      handleRemoveFilterPill(event, btn) {
        event.preventDefault();
        event.stopPropagation();
        const filterParam = btn.dataset.filterParam;
        const filterValue = btn.dataset.filterValue || '';
        if (!filterParam) return;

        if (filterParam === 'price_custom') {
          this.clearCustomPrice();
          this.hidePriceCustomPanel();
          this.syncActivePills();
          this.syncFilterTriggerBadge();
          this.syncSidebarMirrorControls();
          this.updateModalGroupActiveState('price', this.getFilterValues('price'));
          const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
          this.syncCollectionPillHrefs(searchParams);
          this.updateUrlAndSubmitImmediate(searchParams);
          return;
        }

        if (filterParam === 'duration_custom') {
          this.clearCustomDuration();
          this.hideDurationCustomPanel();
          this.syncActivePills();
          this.syncFilterTriggerBadge();
          this.syncSidebarMirrorControls();
          this.updateModalGroupActiveState('duration', this.getFilterValues('duration'));
          const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
          this.syncCollectionPillHrefs(searchParams);
          this.updateUrlAndSubmitImmediate(searchParams);
          return;
        }

        // Remove just this specific value from the selection array
        const current = this.getFilterValues(filterParam);
        this.setFilterValues(filterParam, current.filter(v => v !== filterValue));

        if (filterParam === 'collection_slug') {
          this.syncCollectionPillActiveStates();
        } else {
          this.updateModalGroupActiveState(filterParam, this.getFilterValues(filterParam));
        }

        this.syncActivePills();
        this.syncFilterTriggerBadge();
        this.syncSidebarMirrorControls(filterParam === 'group_size');

        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.syncCollectionPillHrefs(searchParams);
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      // Update active-state class on modal option buttons for a filter group.
      // activeValues is an array of currently selected values.
      updateModalGroupActiveState(filterParam, activeValues) {
        if (!this.filtersModalEl) return;
        const selected = Array.isArray(activeValues) ? activeValues : [];
        this.filtersModalEl.querySelectorAll(`[data-filter-param="${filterParam}"]`).forEach(btn => {
          const btnValue = btn.dataset.filterValue || '';
          btn.classList.toggle('filter-option-btn--active', selected.includes(btnValue));
        });
      }

      syncModalOptionStates() {
        if (!this.filtersModalEl) return;
        Object.keys(this.filterInputIds).forEach(param => {
          this.updateModalGroupActiveState(param, this.getFilterValues(param));
        });

        if (this.hasCustomPriceFilter()) {
          this.showPriceCustomPanel();
          this.filtersModalEl.querySelectorAll('[data-filter-param="price"]').forEach(btn => {
            if (!btn.hasAttribute('data-price-custom-toggle')) {
              btn.classList.remove('filter-option-btn--active');
            }
          });
        } else {
          this.hidePriceCustomPanel();
        }

        if (this.hasCustomDurationFilter()) {
          this.showDurationCustomPanel();
          this.filtersModalEl.querySelectorAll('[data-filter-param="duration"]').forEach(btn => {
            if (!btn.hasAttribute('data-duration-custom-toggle')) {
              btn.classList.remove('filter-option-btn--active');
            }
          });
        } else {
          this.hideDurationCustomPanel();
        }
      }

      // Rebuild the active filter pills — one pill per selected value
      syncActivePills() {
        document.querySelectorAll('[data-search-filter-checkbox]').forEach(control => {
          control.checked = this.getFilterValues(control.dataset.searchFilterCheckbox).includes(control.value);
        });
        if (!this.activePillsContainer) return;
        // Snapshot what's showing so only genuinely new pills animate in —
        // the rebuild below recreates survivors, and they must not flash.
        const previousPillKeys = new Set(
          Array.from(this.activePillsContainer.querySelectorAll('.filter-pill--active'))
            .map((pill) => `${pill.dataset.filterParam}|${pill.dataset.filterValue}`)
        );
        this.activePillsContainer.innerHTML = '';
        requestAnimationFrame(() => {
          const row = document.getElementById('filterPillsRow');
          if (row) this.updatePillsScrollState(row);
        });
        Object.keys(this.filterInputIds).forEach(param => {
          this.getFilterValues(param).forEach(value => {
            if (param === 'type' && this.premiseTypes.includes(value)) return;
            let label = value;
            if (param === 'collection_slug') {
              label = this.collectionNames[value] || value;
            } else if (this.filterValueLabels[param] && this.filterValueLabels[param][value]) {
              label = this.filterValueLabels[param][value];
            }
            this.activePillsContainer.appendChild(this.buildActivePill(param, value, label));
          });
        });

        if (this.hasCustomPriceFilter()) {
          this.activePillsContainer.appendChild(
            this.buildActivePill('price_custom', 'custom', this.customPriceLabel())
          );
        }

        if (this.hasCustomDurationFilter()) {
          this.activePillsContainer.appendChild(
            this.buildActivePill('duration_custom', 'custom', this.customDurationLabel())
          );
        }

        this.activePillsContainer.querySelectorAll('.filter-pill--active').forEach((pill) => {
          const key = `${pill.dataset.filterParam}|${pill.dataset.filterValue}`;
          if (previousPillKeys.has(key)) return;
          pill.classList.add('ds-anim-fade-up');
          pill.addEventListener(
            'animationend',
            () => pill.classList.remove('ds-anim-fade-up'),
            { once: true }
          );
        });
      }

      buildActivePill(filterParam, filterValue, label) {
        const span = document.createElement('span');
        span.className = 'collection-pill filter-pill--active';
        span.dataset.filterParam = filterParam;
        span.dataset.filterValue = filterValue;
        span.appendChild(document.createTextNode(label + ' '));
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'filter-pill__remove';
        removeBtn.setAttribute('aria-label', `Remove ${label} filter`);
        removeBtn.dataset.filterParam = filterParam;
        removeBtn.dataset.filterValue = filterValue;
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', (e) => this.handleRemoveFilterPill(e, removeBtn));
        span.appendChild(removeBtn);
        return span;
      }

      syncFilterTriggerBadge() {
        const trigger = document.querySelector('.filter-pill--trigger');
        if (!trigger) return;
        const total = this.countActiveFilterValues();
        let badge = trigger.querySelector('.filter-pill__badge');
        if (total > 0) {
          trigger.classList.add('filter-pill--trigger--active');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'filter-pill__badge';
            trigger.appendChild(badge);
          }
          badge.textContent = total;
        } else {
          trigger.classList.remove('filter-pill--trigger--active');
          if (badge) badge.remove();
        }
      }

      handlePriceCustomToggleClick(event, btn) {
        event.preventDefault();
        const isVisible = this.priceCustomPanel?.classList.contains('is-visible');

        if (isVisible) {
          this.hidePriceCustomPanel();
          btn.classList.remove('filter-option-btn--active');

          if (this.hasCustomPriceFilter()) {
            this.clearCustomPrice();
            this.syncActivePills();
            this.syncFilterTriggerBadge();
            this.updateModalGroupActiveState('price', this.getFilterValues('price'));
            const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
            this.syncCollectionPillHrefs(searchParams);
            this.updateUrlAndSubmitImmediate(searchParams);
          } else {
            this.updateModalGroupActiveState('price', this.getFilterValues('price'));
          }
          return;
        }

        this.showPriceCustomPanel();
        btn.classList.add('filter-option-btn--active');

        if (this.hasCustomPriceFilter()) {
          this.updateModalGroupActiveState('price', []);
          this.filtersModalEl?.querySelectorAll('[data-filter-param="price"]').forEach(priceBtn => {
            if (!priceBtn.hasAttribute('data-price-custom-toggle')) {
              priceBtn.classList.remove('filter-option-btn--active');
            }
          });
        }
      }

      showPriceCustomPanel() {
        if (!this.priceCustomPanel) return;
        this.priceCustomPanel.hidden = false;
        this.priceCustomPanel.classList.add('is-visible');
        if (this.priceCustomToggle) {
          this.priceCustomToggle.classList.add('filter-option-btn--active');
          this.priceCustomToggle.setAttribute('aria-expanded', 'true');
          this.priceMinInput?.focus();
        }
      }

      hidePriceCustomPanel() {
        if (!this.priceCustomPanel) return;
        // Without a toggle button the range panel is permanent (matches the sidebar).
        if (!this.priceCustomToggle) return;
        this.priceCustomPanel.hidden = true;
        this.priceCustomPanel.classList.remove('is-visible');
        this.priceCustomToggle.classList.remove('filter-option-btn--active');
        this.priceCustomToggle.setAttribute('aria-expanded', 'false');
      }

      hasCustomPriceFilter() {
        const min = this.priceMinInput?.value?.trim();
        const max = this.priceMaxInput?.value?.trim();
        return Boolean(min || max);
      }

      customPriceLabel() {
        const min = this.priceMinInput?.value?.trim();
        const max = this.priceMaxInput?.value?.trim();
        const parts = [];
        if (min) parts.push(`$${min}`);
        if (max) parts.push(`$${max}`);
        return parts.length > 0 ? parts.join(' – ') : 'Custom price';
      }

      clearCustomPrice() {
        if (this.priceMinInput) this.priceMinInput.value = '';
        if (this.priceMaxInput) this.priceMaxInput.value = '';
      }

      syncCustomPriceSearchParams(searchParams) {
        ['min', 'max'].forEach(bound => {
          const header = document.getElementById(`discover-search-budget-${bound}`);
          const input = bound === 'min' ? this.priceMinInput : this.priceMaxInput;
          if (header && input) header.value = input.value;
        });
        const min = this.priceMinInput?.value?.trim();
        const max = this.priceMaxInput?.value?.trim();

        if (min) {
          searchParams.set('price_min', min);
        } else {
          searchParams.delete('price_min');
        }

        if (max) {
          searchParams.set('price_max', max);
        } else {
          searchParams.delete('price_max');
        }
      }

      applyCustomPriceFilter({ fromSearchBar = false } = {}) {
        if (!fromSearchBar && !this.priceCustomPanel?.classList.contains('is-visible')) return;

        const min = this.priceMinInput?.value?.trim();
        const max = this.priceMaxInput?.value?.trim();

        if (!min && !max) {
          this.syncActivePills();
          this.syncFilterTriggerBadge();
          const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
          searchParams.delete('price_min');
          searchParams.delete('price_max');
          this.syncCollectionPillHrefs(searchParams);
          this.updateUrlAndSubmitImmediate(searchParams);
          return;
        }

        this.setFilterValues('price', []);
        this.updateModalGroupActiveState('price', []);
        this.filtersModalEl?.querySelectorAll('[data-filter-param="price"]').forEach(priceBtn => {
          if (!priceBtn.hasAttribute('data-price-custom-toggle')) {
            priceBtn.classList.remove('filter-option-btn--active');
          }
        });
        if (this.priceCustomToggle) {
          this.priceCustomToggle.classList.add('filter-option-btn--active');
        }

        this.syncActivePills();
        this.syncFilterTriggerBadge();

        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      handleDurationCustomToggleClick(event, btn) {
        event.preventDefault();
        const isVisible = this.durationCustomPanel?.classList.contains('is-visible');

        if (isVisible) {
          this.hideDurationCustomPanel();
          btn.classList.remove('filter-option-btn--active');

          if (this.hasCustomDurationFilter()) {
            this.clearCustomDuration();
            this.syncActivePills();
            this.syncFilterTriggerBadge();
            this.updateModalGroupActiveState('duration', this.getFilterValues('duration'));
            const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
            this.syncCollectionPillHrefs(searchParams);
            this.updateUrlAndSubmitImmediate(searchParams);
          } else {
            this.updateModalGroupActiveState('duration', this.getFilterValues('duration'));
          }
          return;
        }

        this.showDurationCustomPanel();
        btn.classList.add('filter-option-btn--active');

        if (this.hasCustomDurationFilter()) {
          this.updateModalGroupActiveState('duration', []);
          this.filtersModalEl?.querySelectorAll('[data-filter-param="duration"]').forEach(durationBtn => {
            if (!durationBtn.hasAttribute('data-duration-custom-toggle')) {
              durationBtn.classList.remove('filter-option-btn--active');
            }
          });
        }
      }

      showDurationCustomPanel() {
        if (!this.durationCustomPanel) return;
        this.durationCustomPanel.hidden = false;
        this.durationCustomPanel.classList.add('is-visible');
        if (this.durationCustomToggle) {
          this.durationCustomToggle.classList.add('filter-option-btn--active');
          this.durationCustomToggle.setAttribute('aria-expanded', 'true');
        }
        this.durationMinInput?.focus();
      }

      hideDurationCustomPanel() {
        if (!this.durationCustomPanel) return;
        this.durationCustomPanel.hidden = true;
        this.durationCustomPanel.classList.remove('is-visible');
        if (this.durationCustomToggle) {
          this.durationCustomToggle.classList.remove('filter-option-btn--active');
          this.durationCustomToggle.setAttribute('aria-expanded', 'false');
        }
      }

      hasCustomDurationFilter() {
        const min = this.durationMinInput?.value?.trim();
        const max = this.durationMaxInput?.value?.trim();
        return Boolean(min || max);
      }

      customDurationLabel() {
        const min = this.durationMinInput?.value?.trim();
        const max = this.durationMaxInput?.value?.trim();
        const parts = [];
        if (min) parts.push(`${min} min`);
        if (max) parts.push(`${max} min`);
        return parts.length > 0 ? parts.join(' – ') : 'Custom duration';
      }

      clearCustomDuration() {
        if (this.durationMinInput) this.durationMinInput.value = '';
        if (this.durationMaxInput) this.durationMaxInput.value = '';
      }

      syncCustomDurationSearchParams(searchParams) {
        const min = this.durationMinInput?.value?.trim();
        const max = this.durationMaxInput?.value?.trim();

        if (min) {
          searchParams.set('duration_min', min);
        } else {
          searchParams.delete('duration_min');
        }

        if (max) {
          searchParams.set('duration_max', max);
        } else {
          searchParams.delete('duration_max');
        }
      }

      applyCustomDurationFilter() {
        if (!this.durationCustomPanel?.classList.contains('is-visible')) return;

        const min = this.durationMinInput?.value?.trim();
        const max = this.durationMaxInput?.value?.trim();

        if (!min && !max) {
          this.syncActivePills();
          this.syncFilterTriggerBadge();
          const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
          searchParams.delete('duration_min');
          searchParams.delete('duration_max');
          this.syncCollectionPillHrefs(searchParams);
          this.updateUrlAndSubmitImmediate(searchParams);
          return;
        }

        this.setFilterValues('duration', []);
        this.updateModalGroupActiveState('duration', []);
        this.filtersModalEl?.querySelectorAll('[data-filter-param="duration"]').forEach(durationBtn => {
          if (!durationBtn.hasAttribute('data-duration-custom-toggle')) {
            durationBtn.classList.remove('filter-option-btn--active');
          }
        });
        if (this.durationCustomToggle) {
          this.durationCustomToggle.classList.add('filter-option-btn--active');
        }

        this.syncActivePills();
        this.syncFilterTriggerBadge();

        this.showLoadingSpinner();
        const searchParams = this.buildMultiFilterSearchParams(this.updateUrlParams());
        this.updateUrlAndSubmitImmediate(searchParams);
      }

      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }

      // ---- End filter modal methods ----

      initializePaginationLinks() {
        // Remove any existing pagination listeners to avoid duplicates
        document.querySelectorAll('.pagination .page-link').forEach(link => {
          const newLink = link.cloneNode(true);
          link.parentNode.replaceChild(newLink, link);
        });
        
        document.querySelectorAll('.pagination .page-link').forEach(link => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const page = e.target.closest('.page-link').dataset.page;
            if (!this.filtersForm) {
              console.error('filters_form not found!');
              return;
            }
            this.updateResults(page, true); // Pass scrollToTop flag
          });
        });
      }

      getCurrentPageFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const pageParam = urlParams.get('page');
        return pageParam ? parseInt(pageParam, 10) : 1;
      }

      syncCollectionStateFromUrl(urlString = window.location.href) {
        const url = new URL(urlString, window.location.origin);

        // Sync all filter inputs from the URL (includes collection_slug)
        this.syncActivePillsFromUrl(url.searchParams);

        // Update collection pill active states
        this.syncCollectionPillActiveStates();
        this.syncFilterTriggerBadge();
      }

      syncActivePillsFromUrl(searchParams) {
        // After an AJAX filter response the URL has been updated; rebuild active pills from it
        if (!this.activePillsContainer) return;
        // Restore hidden inputs from URL params (supports single string or JSON array)
        Object.keys(this.filterInputIds).forEach(param => {
          const raw = searchParams.get(param) || '';
          let values = [];
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              values = Array.isArray(parsed) ? parsed.filter(Boolean) : (parsed ? [String(parsed)] : []);
            } catch {
              values = [raw];
            }
          }
          if (param === 'type' && !raw) values = this.premiseTypes;
          this.setFilterValues(param, values);
        });
        this.syncActivePills();
        this.updateApplyButtonLabel();
      }

      // Collection pills are now buttons (not links), no hrefs to sync.
      // Kept as a no-op to avoid breaking any remaining call sites.
      syncCollectionPillHrefs() {}
    }

    function initializeFiltersTopBarSentinel() {
      const filtersTopBar = document.querySelector('.filters-top-bar');
      if (!filtersTopBar || filtersTopBar.dataset.stickyInitialized === 'true') return;

      const existingSentinel = filtersTopBar.parentNode.querySelector('.filters-top-bar__sentinel');
      if (existingSentinel) {
        existingSentinel.remove();
      }

      const sentinel = document.createElement('div');
      sentinel.className = 'filters-top-bar__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      filtersTopBar.parentNode.insertBefore(sentinel, filtersTopBar);

      const topOffset = parseFloat(getComputedStyle(filtersTopBar).top) || 0;
      const updateState = (entry) => {
        const shouldStick = entry.boundingClientRect.bottom <= topOffset;
        filtersTopBar.classList.toggle('is-stuck', shouldStick);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.length > 0) {
            updateState(entries[0]);
          }
        }, { threshold: [0] });

        observer.observe(sentinel);
        requestAnimationFrame(() => {
          updateState({ boundingClientRect: sentinel.getBoundingClientRect() });
        });
      } else {
        filtersTopBar.classList.add('is-stuck');
      }

      filtersTopBar.dataset.stickyInitialized = 'true';
    }

    function mountProgressiveFiltersManager() {
      if (!document.getElementById('filters_form')) return;

      const manager = new ProgressiveFiltersManager();
      initializeFiltersTopBarSentinel();

      // Group-size total estimates: expose for discover.js (load-more appends)
      // and apply once for any guests value restored on page load.
      window.updateDiscoverPriceEstimates = () => manager.updatePriceEstimates();
      manager.updatePriceEstimates();
    }

    document.addEventListener('filters:rendered', mountProgressiveFiltersManager, { once: false });
    mountProgressiveFiltersManager();
  });

// Keep the utility function available globally for compatibility
function getCurrentPageFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const pageParam = urlParams.get('page');
  return pageParam ? parseInt(pageParam, 10) : 1;
}
